import type { NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { showToastOnApolloErrorImplementation } from '@application/utils';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import fetch from 'cross-fetch';
import type { TFunction } from 'i18next';
import { isProduction } from '@application/env';
import { DebounceLink } from './DebounceLink';
import { authLink } from './auth';
import { cache } from './cache';
import { endpoint } from './endpoint';
import type { BrowserClientContext, ServerClientContext } from './types';

const DEFAULT_DEBOUNCE_TIMEOUT = 100;

const generalLink = (token?: string) => {
  return ApolloLink.from([authLink(token)]);
};

/**
 * BROWSER
 */

const browserHttpLink = (uri: string) => createUploadLink({ uri, fetch, credentials: 'include' });

const browserErrorLink = (translate: TFunction) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    const isMutation = operation.query.definitions.find((x) => (x as any).operation === 'mutation');
    const isLoginMutation = operation.operationName === 'AccountLogin';
    const isCreateMutation = operation.operationName === 'AccountCreate';

    if (graphQLErrors && isMutation) {
      graphQLErrors.forEach((error) => {
        const shouldUseSocialLogin = ['login/use-google', 'login/use-facebook'].includes(
          error.message,
        );

        // when error is "use-social" delegate to component to display modal
        if ((isLoginMutation || isCreateMutation) && shouldUseSocialLogin) {
          return;
        }

        showToastOnApolloErrorImplementation(translate, error as any);
      });
    }
    if (networkError) console.log(`[Network error]: ${networkError}`);
  });

export const browserClient = (context: BrowserClientContext) => {
  const { apolloState, token, translate } = context;

  return new ApolloClient({
    connectToDevTools: !isProduction(),
    cache: cache().restore(apolloState),
    link: new DebounceLink(DEFAULT_DEBOUNCE_TIMEOUT)
      .concat(generalLink(token))
      .concat(browserErrorLink(translate))
      .concat(browserHttpLink(endpoint())),
  });
};

/**
 * SERVER
 */
const serverHttpLink = (uri: string) => createHttpLink({ uri, fetch, credentials: 'include' });

const serverErrorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
    );
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const logLink = new ApolloLink((operation, forward) => {
  const log = `[APOLLO] ${operation.operationName}`;
  console.time(log);
  return forward(operation).map((result) => {
    console.timeEnd(log);
    return result;
  });
});

export const serverClient = (context: ServerClientContext): ApolloClient<NormalizedCacheObject> => {
  const { token } = context;

  if (!isProduction()) {
    return new ApolloClient({
      ssrMode: true,
      cache: cache(),
      link: generalLink(token)
        .concat(logLink)
        .concat(serverErrorLink)
        .concat(serverHttpLink(endpoint())),
    });
  }

  return new ApolloClient({
    ssrMode: true,
    cache: cache(),
    link: generalLink(token).concat(serverErrorLink).concat(serverHttpLink(endpoint())),
  });
};
